<template>
  <UserDrawer ref="drawerComponent" v-model="drawer" @user-added="callApi" v-model:editUser="editUser" />
  <ChangePassword ref="drawerComponentPassword" v-model="drawerPassword" v-model:editUser="editUser" />
  <v-container>
    <div class="exploration-table fd-table fd-settings-users-table">
      <div class="table-top-content">
        <div class="d-flex align-center">
          <div class="title">Users &amp; Roles<span class="userCount">{{ filteredUsers.length }} users</span></div>
        </div>
        <div class="fd-settings-toolbar">
          <div class="search-container">
            <v-text-field
              ref="usersSearch"
              v-model="searchQuery"
              placeholder="Search"
              variant="outlined"
              density="compact"
              clearable
              hide-details
              class="search-input"
            >
              <template #prepend-inner>
                <img :src="searchIcon" alt="" width="16" height="16" class="fd-action-icon" />
              </template>
            </v-text-field>
          </div>
          <div class="test-pdf-div">
            <v-btn class="pdf-btn add-user-btn" @click="openSideModal">
              <span><img :src="addIcon" alt="" width="16" height="16" class="fd-action-icon" /> Add User</span>
            </v-btn>
          </div>
        </div>
      </div>
      <!-- Add a scrollable wrapper around the table -->
      <div class="table-scroll-wrapper">
        <TabularLoaders v-if="loading" :columns="5" :rows="8" row-variant="user" />
        <v-data-table-server
          v-else
          v-model:items-per-page="itemsPerPage"
          :headers="headers"
          :items="paginatedUsers"
          :items-per-page-options="pageOptions"
          :items-length="filteredUsers.length"
          :loading="false"
          item-value="name"
          hide-default-footer
          @update:options="handlePageChange"
          fixed-header
          :height="tableScrollHeight"
        >
          <template v-slot:headers>
            <tr>
              <th v-for="header in headers" :key="header.key" class="text-left header-cell">
                <div class="d-flex align-center">
                  <div class="sort-icon-title d-flex align-center fd-settings-sort-title">
                    {{ header.title }}
                  </div>
                </div>
              </th>
            </tr>
          </template>
          <template v-slot:item="{ item }">
            <tr>
              <td>
                <div class="user-container d-flex align-center">
                  <!-- User Avatar with Microsoft Icon -->
                  <div class="user-avatar">
                    <v-avatar size="40" class="user-avatar-img">
                      <v-img :src=userImage alt="User Avatar"></v-img>
                    </v-avatar>
                    <v-img  v-if="item.loginType != 'local'" :src=companyIcon alt="Microsoft Logo" class="ms-icon ms-icon-img" width="16" height="16" />
                  </div>
                  <!-- User Details -->
                  <div class="user-details">
                    <div class="d-flex align-center">
                      <span class="user-name-table">{{ item.firstName }} {{ item.lastName }}</span>
                    </div>
                    <span class="user-username">{{ item.username }}</span>
                  </div>
                </div>
              </td>
              <td>
                <span
                  class="status-chip"
                  :class="{ 'status-chip--inactive': isUserInactive(item) }"
                >{{ isUserInactive(item) ? 'Inactive' : 'Active' }}</span>
              </td>
              <td>
                <div class="fd-settings-chip-wrap">
                  <span
                    v-if="item.role"
                    class="fd-settings-chip"
                    :class="roleChipClass(item.role)"
                  >{{ item.role }}</span>
                </div>
              </td>
              <td class="fd-settings-chip-cell">
                <div class="fd-settings-chip-wrap">
                  <span v-for="tenant in item.tenants" :key="tenant.id" class="fd-settings-tenant">
                    {{ tenant.name }}
                  </span>
                </div>
              </td>
              <td class="fd-settings-actions-cell">
                <div class="fd-settings-row-actions">
                  <button type="button" class="fd-settings-action-btn" @click="editUserModal(item)">
                    <img :src="pencil" alt="Edit" />
                  </button>
                  <button type="button" class="fd-settings-action-btn fd-settings-action-btn--delete" @click="showDeleteConfirmation(item)">
                    <img :src="delete_icon" alt="Delete" />
                  </button>
                  <button
                    v-if="item.loginType === 'local'"
                    type="button"
                    class="fd-settings-action-btn"
                    @click="openChangePasswordModal(item)"
                  >
                    <img :src="changePass" alt="Change password" />
                  </button>
                </div>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <div class="d-flex justify-center align-center pa-4">
              No data available
            </div>
          </template>
        </v-data-table-server>
      </div>
      <Pagination
        v-if="!loading && filteredUsers.length > 0"
        variant="scorecard"
        :total-items="filteredUsers.length"
        v-model:current-page="page"
        v-model:items-per-page="itemsPerPage"
        item-label="Users"
        :page-sizes="pageOptions"
      />
    </div>
    <v-snackbar v-model="snackbar.visible" :color="snackbar.color" :timeout="snackbar.timeout">
      {{ snackbar.message }}
    </v-snackbar>
    
    <!-- Delete Confirmation Modal -->
    <v-dialog transition="dialog-top-transition" v-model="confirmDeleteDialog" width="417">
      <div class="cross-btn-div" @click="closeDeleteConfirmation">
        <img :src="close_icon" alt="close" width="20" height="20" class="fd-action-icon" />
      </div>
      <v-alert title="Delete User" :text="confirmDeleteText">
        <template #prepend>
          <div class="fd-delete-modal-icon">
            <img :src="delete_icon" alt="" width="24" height="24" class="fd-action-icon" />
          </div>
        </template>
        <div class="alert-btns-div">
          <v-btn class="cancel-btn" flat @click="closeDeleteConfirmation">Cancel</v-btn>
          <v-btn class="delete-btn" flat @click="confirmDeleteUser" :loading="deleteLoading">
            Delete</v-btn>
        </div>
      </v-alert>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "../../../axiosInterceptor";
import axios from "axios";
import UserDrawer from "@/components/layout/Users/UserDrawer.vue";
import ChangePassword from "./ChangePassword.vue";
import Pagination from "@/components/layout/Common/Pagination.vue";
import TabularLoaders from "@/components/layout/Loaders/TabularLoaders.vue";
export default {
  name: "TableData",
  components: {
    UserDrawer,
    ChangePassword,
    Pagination,
    TabularLoaders,
  },
  props: {
    users: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sort_up: require("../../../assets/Svg/Table/up.svg"),
      sort_down: require("../../../assets/Svg/Table/down.svg"),
      reset_icon: require("../../../assets/Svg/Icons/reset.svg"),
      searchIcon: require("../../../assets/Svg/Icons/search.svg"),
      addIcon: require("../../../assets/Svg/Icons/add.svg"),
      close_icon: require("../../../assets/Svg/Icons/cross.svg"),
      elipse: require("../../../assets/Svg/elipse.svg"),
      pencil: require("../../../assets/Svg/Icons/edit-2.svg"),
      delete_icon: require("../../../assets/Svg/trash.svg"),
      userImage: require("../../../assets/Images/Navbar/profile.png"),
      companyIcon: require("../../../assets/Svg/companyIcon.svg"),
      changePass: require("../../../assets/Svg/Password.svg"),
      itemsPerPage: Number(localStorage.getItem('itemsPerPage')) || 20,
      headers: [
        { title: 'Users', key: 'firstname', sortable: false },
        { title: 'Status', key: 'status', sortable: false },
        { title: 'Roles', key: 'roles', sortable: false },
        { title: 'Tenant', key: 'tenant', sortable: false },
        { title: 'Action', key: 'action', sortable: false }
      ],
      totalItems: 0,
      page: 1,
      pageOptions: [20, 50, 100, 200, 300, 400, 500],
      searchQuery: '',
      searchLoading: false,
      loadingCsv: false,
      drawer: false,
      drawerPassword: false,
      user: {
        name: '',
        email: '',
        role: '',
      },
      snackbar: {
        visible: false,
        message: "",
        color: "success",
        timeout: 3000,
      },
      editUser: "",
      confirmDeleteDialog: false,
      confirmDeleteText: "",
      deleteLoading: false,
      selectedUser: null
    };
  },
  computed: {
    tableScrollHeight() {
      return "100%";
    },
    serverItems() {
      return this.users; // Directly mapping props to computed property
    },
    filteredUsers() {
      if (!this.searchQuery) {
        return this.users;
      }
      
      const query = this.searchQuery.toLowerCase();
      return this.users.filter(user => {
        const firstNameMatch = user.firstName && user.firstName.toLowerCase().includes(query);
        
        const lastNameMatch = user.lastName && user.lastName.toLowerCase().includes(query);
        
        const usernameMatch = user.username && user.username.toLowerCase().includes(query);
        
        const roleMatch = user.role && user.role.toLowerCase().includes(query);
        
        const tenantsMatch = user.tenants && user.tenants.some(tenant => 
          tenant.name && tenant.name.toLowerCase().includes(query)
        );
        
        return firstNameMatch || lastNameMatch || usernameMatch || roleMatch || tenantsMatch;
      });
    },
    paginatedUsers() {
      const perPage = Number(this.itemsPerPage) || 20;
      const start = (this.page - 1) * perPage;
      return this.filteredUsers.slice(start, start + perPage);
    }
  },
  methods: {
    roleChipClass(role) {
      const key = String(role || "").toLowerCase();
      if (key.includes("admin")) return "fd-settings-chip--admin";
      if (key.includes("manager")) return "fd-settings-chip--manager";
      if (key.includes("viewer")) return "fd-settings-chip--viewer";
      return "";
    },
    isUserInactive(item) {
      if (!item) return false;
      if (item.isActive === false || item.active === false || item.enabled === false) return true;
      const status = String(item.status || "").toLowerCase();
      return status === "inactive" || status === "disabled";
    },
    callApi() {
      this.$emit("user-api-call");
    },
    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },
    openSideModal() {
      this.$refs.drawerComponent.resetForm();
      this.editUser = null;
      this.drawer = true;
    },
    openChangePasswordModal(item) {
      this.editUser = item
      this.drawerPassword = true;
    },
    editUserModal(item) {
      this.editUser = item
      this.drawer = true;
    },
    handlePageChange({ page, itemsPerPage }) {
      this.$emit('update:pagination', { page, itemsPerPage });
      localStorage.setItem('itemsPerPage', itemsPerPage);
    },
    showDeleteConfirmation(item) {
      this.selectedUser = item;
      this.confirmDeleteText = `Are you sure you want to delete the user "${item.firstName} ${item.lastName}"? This action cannot be undone.`;
      this.confirmDeleteDialog = true;
    },
    closeDeleteConfirmation() {
      this.confirmDeleteDialog = false;
      this.selectedUser = null;
      this.deleteLoading = false;
    },
    async confirmDeleteUser() {
      if (!this.selectedUser) return;
      
      this.deleteLoading = true;
      const userId = this.selectedUser.id;

      try {
        const response = await api.delete(`${process.env.VUE_APP_API_URL}public/api/admin/users/${userId}`);

        if (response && response.data) {
          if (response.data.statusCode === 200 || response.data.statusCode === 201) {
            this.showSnackbar(response.data.message || "User deleted successfully!", "success");
            this.$emit("user-api-call");
            this.closeDeleteConfirmation();
          } else {
            this.showSnackbar(response.data.message || "Error deleting user", "error");
          }
        } else {
          this.showSnackbar("Unexpected response from server", "error");
        }
      } catch (error) {
        this.showSnackbar(error.response?.data?.message || "Error deleting user", "error");
      } finally {
        this.deleteLoading = false;
      }
    }

  },
  watch: {
    users: {
      handler(newVal) {
        this.totalItems = newVal.length; // Update total items count
      },
      immediate: true,
      deep: true
    },
    searchQuery() {
      this.page = 1;
    },
    itemsPerPage() {
      this.page = 1;
    }
  }
};
</script>
